import React, { useState, useEffect, useMemo } from 'react';
import logger from '../lib/logger';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { useAuth } from '../contexts/AuthContext';
import api, { getErrorMessage } from '../lib/api';
import { getExamName } from '../config/examSubjects';
import { 
  Brain, Lock, AlertCircle, CheckCircle,
  BookOpen, ChevronDown, ChevronUp,
  Lightbulb, Sparkles, XCircle, Flame,
  GraduationCap, Filter, Clock, Target,
  TrendingUp, TrendingDown, Calendar, BarChart3,
  RefreshCw, Zap, Trophy, ArrowRight, ArrowUp,
  ArrowDown, PieChart, History, CalendarClock,
  Repeat, Star, AlertTriangle, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import { renderLatex } from '../lib/renderLatex';
import LoadingSpinner from '../components/LoadingSpinner';

const Mistakes = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mistakes, setMistakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [expandedChapters, setExpandedChapters] = useState({});
  const [activeTab, setActiveTab] = useState('overview'); // overview, patterns, test-mistakes, practice-mistakes, spaced, questions
  
  // Filters
  const [filterExam, setFilterExam] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterChapter, setFilterChapter] = useState('all');
  const [filterMistakeType, setFilterMistakeType] = useState('all');
  const [filterSourceType, setFilterSourceType] = useState('all'); // 'all', 'test', 'practice'

  // Smart Revision Modal
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionSubjects, setRevisionSubjects] = useState([]);
  const [revisionChapters, setRevisionChapters] = useState([]);
  const [revisionNumQuestions, setRevisionNumQuestions] = useState(10);

  const isPremium = user?.premium;

  useEffect(() => {
    loadMistakes();
  }, [filterSourceType, filterExam, filterSubject, filterChapter]);

  const loadMistakes = async () => {
    try {
      const params = {};
      if (filterSourceType !== 'all') params.source_type = filterSourceType;
      if (filterExam !== 'all') params.exam_id = filterExam;
      if (filterSubject !== 'all') params.subject = filterSubject;
      if (filterChapter !== 'all') params.chapter = filterChapter;
      
      const response = await api.get('/mistakes', { params });
      setMistakes(response.data);
    } catch (error) {
      logger.error('Failed to load mistakes');
    } finally {
      setLoading(false);
    }
  };

  // Analyze mistakes - get all unique exams, subjects, chapters with counts
  const analysis = useMemo(() => {
    const exams = {};
    const subjects = {};
    const chapters = {};
    
    // Advanced metrics
    const mistakeTypes = { careless: 0, conceptual: 0, timeRelated: 0, unknown: 0 };
    const difficultyBreakdown = { easy: 0, medium: 0, hard: 0 };
    const repeatMistakes = [];
    const recentMistakes = [];
    const oldMistakes = [];
    
    const now = new Date();
    const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
    const oneMonthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);

    mistakes.forEach(item => {
      const q = item.question;
      if (!q) return;

      const examId = q.exam_id || 'general';
      const examName = getExamName(examId) || 'General Practice';
      const subjectName = q.subject_name || q.subject_id || 'General';
      const chapterName = q.chapter || 'Mixed Topics';
      const difficulty = q.difficulty || 'medium';
      
      // Difficulty breakdown
      difficultyBreakdown[difficulty]++;
      
      // Classify mistake type based on available data
      const timeSpent = item.time_spent_seconds || item.time_spent || 0;
      const attempts = item.attempts || 1;
      
      if (difficulty === 'easy' && attempts === 1) {
        mistakeTypes.careless++;
        item.mistakeType = 'careless';
      } else if (timeSpent > 0 && timeSpent < 30) {
        mistakeTypes.timeRelated++;
        item.mistakeType = 'rushed';
      } else if (timeSpent > 180) {
        mistakeTypes.timeRelated++;
        item.mistakeType = 'overthought';
      } else if (attempts > 1) {
        mistakeTypes.conceptual++;
        item.mistakeType = 'conceptual';
      } else {
        mistakeTypes.conceptual++;
        item.mistakeType = 'conceptual';
      }
      
      // Repeat mistakes (attempted multiple times and still wrong)
      if (attempts > 1) {
        repeatMistakes.push(item);
      }
      
      // Time-based categorization
      const mistakeDate = new Date(item.created_at);
      if (mistakeDate >= oneWeekAgo) {
        recentMistakes.push(item);
      } else if (mistakeDate < oneMonthAgo) {
        oldMistakes.push(item);
      }

      // Track exams
      if (!exams[examId]) {
        exams[examId] = { id: examId, name: examName, count: 0 };
      }
      exams[examId].count++;

      // Track subjects
      if (!subjects[subjectName]) {
        subjects[subjectName] = { name: subjectName, count: 0, examId, difficulties: { easy: 0, medium: 0, hard: 0 } };
      }
      subjects[subjectName].count++;
      subjects[subjectName].difficulties[difficulty]++;

      // Track chapters
      const chapterKey = `${subjectName}__${chapterName}`;
      if (!chapters[chapterKey]) {
        chapters[chapterKey] = { 
          name: chapterName, 
          subject: subjectName, 
          examId,
          count: 0,
          difficulties: { easy: 0, medium: 0, hard: 0 },
          avgTimeSpent: 0,
          totalTime: 0
        };
      }
      chapters[chapterKey].count++;
      chapters[chapterKey].difficulties[difficulty]++;
      if (timeSpent > 0) {
        chapters[chapterKey].totalTime += timeSpent;
        chapters[chapterKey].avgTimeSpent = Math.round(chapters[chapterKey].totalTime / chapters[chapterKey].count);
      }
    });

    // Sort by count (most mistakes first)
    const sortedExams = Object.values(exams).sort((a, b) => b.count - a.count);
    const sortedSubjects = Object.values(subjects).sort((a, b) => b.count - a.count);
    const sortedChapters = Object.values(chapters).sort((a, b) => b.count - a.count);

    // Top weak areas (chapters with 2+ mistakes)
    const weakAreas = sortedChapters.filter(c => c.count >= 2).slice(0, 6);
    
    // Calculate spaced repetition due items
    const spacedRepetition = mistakes.map(item => {
      const mistakeDate = new Date(item.created_at);
      const daysSinceMistake = Math.floor((now - mistakeDate) / (24 * 60 * 60 * 1000));
      const attempts = item.attempts || 1;
      
      // Fibonacci-like intervals: 1, 2, 3, 5, 8, 13, 21 days
      const intervals = [1, 2, 3, 5, 8, 13, 21];
      const interval = intervals[Math.min(attempts - 1, intervals.length - 1)];
      const nextReviewDate = new Date(mistakeDate.getTime() + interval * 24 * 60 * 60 * 1000);
      const isDue = now >= nextReviewDate;
      const daysUntilDue = Math.max(0, Math.ceil((nextReviewDate - now) / (24 * 60 * 60 * 1000)));
      
      return {
        ...item,
        daysSinceMistake,
        nextReviewDate,
        isDue,
        daysUntilDue,
        priority: isDue ? (attempts > 1 ? 'high' : 'medium') : 'low'
      };
    }).sort((a, b) => {
      // Sort by priority then by due date
      if (a.isDue && !b.isDue) return -1;
      if (!a.isDue && b.isDue) return 1;
      return a.daysUntilDue - b.daysUntilDue;
    });
    
    const dueForReview = spacedRepetition.filter(item => item.isDue);
    const upcomingReview = spacedRepetition.filter(item => !item.isDue && item.daysUntilDue <= 7);
    
    // Calculate overall improvement potential
    const improvementPotential = {
      carelessFixable: mistakeTypes.careless,
      conceptualNeedStudy: mistakeTypes.conceptual,
      timeManagement: mistakeTypes.timeRelated,
      totalPotentialGain: Math.round((mistakeTypes.careless * 100 + mistakeTypes.timeRelated * 50) / Math.max(1, mistakes.length))
    };

    return { 
      exams: sortedExams || [], 
      subjects: sortedSubjects || [], 
      chapters: sortedChapters || [], 
      weakAreas: weakAreas || [],
      mistakeTypes: mistakeTypes || { careless: 0, conceptual: 0, timeRelated: 0, unknown: 0 },
      difficultyBreakdown: difficultyBreakdown || { easy: 0, medium: 0, hard: 0 },
      repeatMistakes: repeatMistakes || [],
      recentMistakes: recentMistakes || [],
      oldMistakes: oldMistakes || [],
      spacedRepetition: spacedRepetition || [],
      dueForReview: dueForReview || [],
      upcomingReview: upcomingReview || [],
      improvementPotential: improvementPotential || { carelessFixable: 0, conceptualNeedStudy: 0, timeManagement: 0, totalPotentialGain: 0 }
    };
  }, [mistakes]);

  // Filter mistakes based on selected filters
  const filteredMistakes = useMemo(() => {
    return mistakes.filter(item => {
      const q = item.question;
      if (!q) return false;

      if (filterExam !== 'all' && q.exam_id !== filterExam) return false;
      if (filterSubject !== 'all' && (q.subject_name || q.subject_id) !== filterSubject) return false;
      if (filterChapter !== 'all' && q.chapter !== filterChapter) return false;
      if (filterMistakeType !== 'all' && item.mistakeType !== filterMistakeType) return false;

      return true;
    });
  }, [mistakes, filterExam, filterSubject, filterChapter, filterMistakeType]);

  // Get available filter options based on current selection
  const availableSubjects = useMemo(() => {
    if (!analysis.subjects) return [];
    if (filterExam === 'all') return analysis.subjects;
    return analysis.subjects.filter(s => s.examId === filterExam);
  }, [analysis.subjects, filterExam]);

  const availableChapters = useMemo(() => {
    if (!analysis.chapters) return [];
    let filtered = analysis.chapters;
    if (filterExam !== 'all') {
      filtered = filtered.filter(c => c.examId === filterExam);
    }
    if (filterSubject !== 'all') {
      filtered = filtered.filter(c => c.subject === filterSubject);
    }
    return filtered;
  }, [analysis.chapters, filterExam, filterSubject]);

  const handleGenerateRevisionTest = async () => {
    if (!isPremium) {
      toast.error('Upgrade to Premium for Smart Revision');
      navigate('/subscription');
      return;
    }
    
    // Open modal for subject/chapter selection
    setShowRevisionModal(true);
  };

  const generateRevisionTest = async () => {
    try {
      const params = { num_questions: revisionNumQuestions };
      if (filterSourceType !== 'all') params.source_type = filterSourceType;
      if (filterExam !== 'all') params.exam_id = filterExam;
      
      // Add selected subjects
      if (revisionSubjects.length > 0) {
        params.subjects = revisionSubjects.join(',');
      }
      
      // Add selected chapters
      if (revisionChapters.length > 0) {
        params.chapters = revisionChapters.join(',');
      }
      
      const response = await api.post('/mistakes/generate-test', null, { params });
      setShowRevisionModal(false);
      navigate(`/test/${response.data.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate test'));
    }
  };

  const toggleQuestion = (id) => {
    setExpandedQuestions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const clearFilters = () => {
    setFilterSourceType('all');
    setFilterExam('all');
    setFilterSubject('all');
    setFilterChapter('all');
    setFilterMistakeType('all');
  };

  const hasActiveFilters = filterSourceType !== 'all' || filterExam !== 'all' || filterSubject !== 'all' || filterChapter !== 'all' || filterMistakeType !== 'all';

  // Render mistakes section for test/practice tabs
  const renderMistakesSection = (sourceType) => {
    const sourceMistakes = mistakes.filter(m => m.source_type === sourceType);
    
    if (sourceMistakes.length === 0) {
      return (
        <Card className="p-12 text-center">
          <CheckCircle className="text-emerald-500 mx-auto mb-4" size={48} />
          <h3 className="text-xl font-bold text-foreground mb-2">
            No {sourceType === 'test' ? 'Test' : 'Practice'} Mistakes
          </h3>
          <p className="text-muted-foreground">
            You haven't made any mistakes in {sourceType === 'test' ? 'tests' : 'practice sessions'} yet!
          </p>
        </Card>
      );
    }

    // Group by subject and chapter with statistics
    const grouped = sourceMistakes.reduce((acc, mistake) => {
      const subjectName = mistake.question?.subject_name || mistake.question?.subject || 'Unknown';
      const chapterName = mistake.question?.chapter || 'Unknown Chapter';
      const difficulty = mistake.question?.difficulty || 'medium';
      
      if (!acc[subjectName]) {
        acc[subjectName] = {
          chapters: {},
          total: 0,
          difficulties: { easy: 0, medium: 0, hard: 0 }
        };
      }
      if (!acc[subjectName].chapters[chapterName]) {
        acc[subjectName].chapters[chapterName] = {
          mistakes: [],
          difficulties: { easy: 0, medium: 0, hard: 0 }
        };
      }
      
      acc[subjectName].chapters[chapterName].mistakes.push(mistake);
      acc[subjectName].chapters[chapterName].difficulties[difficulty]++;
      acc[subjectName].total++;
      acc[subjectName].difficulties[difficulty]++;
      
      return acc;
    }, {});

    return (
      <div className="space-y-4">
        {Object.entries(grouped)
          .sort(([, a], [, b]) => b.total - a.total)
          .map(([subject, subjectData]) => {
            const subjectKey = `${sourceType}-${subject}`;
            const isSubjectExpanded = expandedSubjects[subjectKey] !== false; // Default to expanded

            return (
              <Card key={subject} className="overflow-hidden">
                <Collapsible
                  open={isSubjectExpanded}
                  onOpenChange={(open) => setExpandedSubjects(prev => ({ ...prev, [subjectKey]: open }))}
                >
                  <CollapsibleTrigger className="w-full">
                    <div className="p-4 hover:bg-accent/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isSubjectExpanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                          <GraduationCap size={20} className="text-primary" />
                          <h3 className="font-bold text-lg text-foreground">{subject}</h3>
                          <Badge variant="secondary">{subjectData.total} mistakes</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 font-medium">
                            {subjectData.difficulties.easy} Easy
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 font-medium">
                            {subjectData.difficulties.medium} Med
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-red-100 dark:bg-red-900/30 text-red-700 font-medium">
                            {subjectData.difficulties.hard} Hard
                          </span>
                          <Badge variant="outline" className="ml-2">
                            {Object.keys(subjectData.chapters).length} chapters
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t border-border">
                      {Object.entries(subjectData.chapters)
                        .sort(([, a], [, b]) => b.mistakes.length - a.mistakes.length)
                        .map(([chapter, chapterData]) => {
                          const chapterKey = `${sourceType}-${subject}-${chapter}`;
                          const isChapterExpanded = expandedChapters[chapterKey] !== false; // Default to expanded

                          return (
                            <div key={chapter} className="border-b border-border last:border-0">
                              <Collapsible
                                open={isChapterExpanded}
                                onOpenChange={(open) => setExpandedChapters(prev => ({ ...prev, [chapterKey]: open }))}
                              >
                                <CollapsibleTrigger className="w-full">
                                  <div className="p-3 pl-12 bg-muted/30 hover:bg-muted/50 transition-colors">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        {isChapterExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                                        <BookOpen size={16} className="text-primary/70" />
                                        <h4 className="font-semibold text-foreground">{chapter}</h4>
                                        <Badge variant="outline" className="text-xs">
                                          {chapterData.mistakes.length} questions
                                        </Badge>
                                      </div>
                                      <div className="flex gap-1">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-600">
                                          {chapterData.difficulties.easy}
                                        </span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600">
                                          {chapterData.difficulties.medium}
                                        </span>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/20 text-red-600">
                                          {chapterData.difficulties.hard}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </CollapsibleTrigger>

                                <CollapsibleContent>
                                  <div className="divide-y divide-border bg-card">
                                    {chapterData.mistakes.map((item, idx) => {
                                      const q = item.question;
                                      if (!q) return null;
                                      
                                      const mistakeType = item.mistake?.mistake_type || 'unknown';
                                      const difficulty = q.difficulty || 'medium';
                                      const isExpanded = expandedQuestions[item.id];

                                      return (
                                        <div key={item.id} className="bg-card">
                                          <button
                                            onClick={() => toggleQuestion(item.id)}
                                            className="w-full flex items-center gap-3 p-4 hover:bg-accent/50 transition-colors text-left"
                                          >
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                              difficulty === 'easy' ? 'bg-green-100 dark:bg-green-900/30' :
                                              difficulty === 'hard' ? 'bg-red-100 dark:bg-red-900/30' :
                                              'bg-yellow-100 dark:bg-yellow-900/30'
                                            }`}>
                                              <span className="text-xs font-bold text-foreground">{idx + 1}</span>
                                            </div>
                                            
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <Badge className={`text-[10px] ${
                                                  difficulty === 'easy' ? 'bg-green-500' :
                                                  difficulty === 'hard' ? 'bg-red-500' : 'bg-yellow-500'
                                                }`}>{difficulty}</Badge>
                                                <Badge className={`text-[10px] ${
                                                  mistakeType === 'careless' ? 'bg-amber-500' :
                                                  mistakeType === 'conceptual' ? 'bg-purple-500' : 'bg-blue-500'
                                                }`}>{mistakeType}</Badge>
                                                {q.year && <span className="text-[10px] text-muted-foreground">PYQ {q.year}</span>}
                                              </div>
                                              <p 
                                                className="text-sm text-foreground line-clamp-2"
                                                dangerouslySetInnerHTML={{ __html: renderLatex(q.question_text || '') }}
                                              />
                                            </div>

                                            <div className="shrink-0">
                                              {isExpanded ? <ChevronUp size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
                                            </div>
                                          </button>

                                          {isExpanded && (
                                            <div className="px-4 pb-4 border-t border-border bg-muted/20">
                                              <div className="py-4">
                                                <p 
                                                  className="text-sm text-foreground leading-relaxed"
                                                  dangerouslySetInnerHTML={{ __html: renderLatex(q.question_text) }}
                                                />
                                              </div>

                                              {q.options && Array.isArray(q.options) && q.options.length > 0 && (
                                                <div className="space-y-2 mb-4">
                                                  {q.options.map((option, optIdx) => {
                                                    const optionLetter = option.label || String.fromCharCode(65 + optIdx);
                                                    const optionText = option.text || option;
                                                    const isCorrect = q.correct_answer === optionLetter;
                                                    const userAnswer = item.user_answer || item.mistake?.user_answer;
                                                    const isUserAnswer = userAnswer === optionLetter;
                                                    const isWrong = isUserAnswer && !isCorrect;

                                                    return (
                                                      <div 
                                                        key={optIdx}
                                                        className={`flex items-start gap-3 p-3 rounded-lg ${
                                                          isCorrect 
                                                            ? 'bg-green-50 dark:bg-green-950/30 border border-green-300 dark:border-green-700' 
                                                            : isWrong 
                                                            ? 'bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-700'
                                                            : 'bg-muted/50 border border-border'
                                                        }`}
                                                      >
                                                        <span className={`font-bold text-sm ${
                                                          isCorrect ? 'text-green-600' : isWrong ? 'text-red-600' : 'text-muted-foreground'
                                                        }`}>
                                                          {optionLetter}.
                                                        </span>
                                                        <span 
                                                          className={`flex-1 text-sm ${
                                                            isCorrect ? 'text-green-700 dark:text-green-300' : 
                                                            isWrong ? 'text-red-700 dark:text-red-300' : 'text-foreground'
                                                          }`}
                                                          dangerouslySetInnerHTML={{ __html: renderLatex(optionText) }}
                                                        />
                                                        {isCorrect && <CheckCircle className="text-green-500 shrink-0" size={18} />}
                                                        {isWrong && <XCircle className="text-red-500 shrink-0" size={18} />}
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              )}

                                              {(!q.options || q.options.length === 0) && (
                                                <div className="grid grid-cols-2 gap-3 mb-4">
                                                  <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                                                    <p className="text-xs font-medium text-red-600 mb-1">Your Answer</p>
                                                    <p className="text-lg font-bold text-red-700 dark:text-red-300">
                                                      {item.user_answer || item.mistake?.user_answer || '—'}
                                                    </p>
                                                  </div>
                                                  <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                                                    <p className="text-xs font-medium text-green-600 mb-1">Correct Answer</p>
                                                    <p className="text-lg font-bold text-green-700 dark:text-green-300">
                                                      {q.correct_answer}
                                                    </p>
                                                  </div>
                                                </div>
                                              )}

                                              {q.explanation && (
                                                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                                  <div className="flex items-center gap-2 mb-2">
                                                    <Lightbulb className="text-blue-500" size={16} />
                                                    <span className="text-sm font-medium text-blue-600">Explanation</span>
                                                  </div>
                                                  <div 
                                                    className="text-sm text-foreground leading-relaxed"
                                                    dangerouslySetInnerHTML={{ __html: renderLatex(q.explanation) }}
                                                  />
                                                </div>
                                              )}

                                              <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                                                <span>Attempts: {item.attempts || 1}</span>
                                                {item.created_at && (
                                                  <span>Last: {new Date(item.created_at).toLocaleDateString()}</span>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          );
                        })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner size="lg" text="Loading your mistakes..." fullScreen={false} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                <TrendingUp className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Mistake Tracker</h1>
                <p className="text-sm text-muted-foreground">Learn from your mistakes to improve</p>
              </div>
            </div>
            <Button 
              onClick={handleGenerateRevisionTest}
              disabled={filteredMistakes.length === 0}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg"
            >
              Smart Revision ({filteredMistakes.length})
              {!isPremium && <Lock size={14} className="ml-2" />}
            </Button>
          </div>
        </div>

        {mistakes.length === 0 ? (
          <Card className="p-12 text-center rounded-2xl border-2 border-dashed border-border">
            <CheckCircle className="text-emerald-500 mx-auto mb-4" size={48} />
            <h3 className="text-xl font-bold text-foreground mb-2">No Mistakes Yet</h3>
            <p className="text-muted-foreground mb-6">
              Take some tests or practice sessions to start tracking your mistakes.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/test/create')}>Take a Test</Button>
              <Button variant="outline" onClick={() => navigate('/exams')}>Practice</Button>
            </div>
          </Card>
        ) : (
          <>
            {/* Tab Navigation */}
            <div className="flex gap-2 mb-6 p-1 bg-muted/50 rounded-xl overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'test-mistakes', label: 'Test Mistakes', icon: Trophy, count: mistakes.filter(m => m.source_type === 'test').length },
                { id: 'practice-mistakes', label: 'Practice Mistakes', icon: Target, count: mistakes.filter(m => m.source_type === 'practice').length },
                { id: 'patterns', label: 'Patterns', icon: Brain },
                { id: 'spaced', label: 'Spaced Review', icon: Clock },
                { id: 'questions', label: 'All Questions', icon: BookOpen }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                    activeTab === tab.id 
                      ? 'bg-background shadow-md text-primary' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                  {tab.count !== undefined && (
                    <Badge variant="secondary" className="text-[10px] px-1.5">{tab.count}</Badge>
                  )}
                  {tab.id === 'spaced' && analysis.dueForReview.length > 0 && (
                    <Badge className="bg-red-500 text-white text-[10px] px-1.5">{analysis.dueForReview.length}</Badge>
                  )}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Enhanced Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <Card className="p-4 bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/30 border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
                        <AlertCircle className="text-white" size={20} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">{mistakes.length}</p>
                        <p className="text-xs text-muted-foreground">Total Mistakes</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                        <Zap className="text-white" size={20} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{analysis.mistakeTypes.careless}</p>
                        <p className="text-xs text-muted-foreground">Careless Errors</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/30 dark:to-violet-950/30 border-purple-200 dark:border-purple-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                        <Brain className="text-white" size={20} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{analysis.mistakeTypes.conceptual}</p>
                        <p className="text-xs text-muted-foreground">Conceptual Gaps</p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                        <Clock className="text-white" size={20} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{analysis.dueForReview.length}</p>
                        <p className="text-xs text-muted-foreground">Due for Review</p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Improvement Potential */}
                <Card className="p-4 mb-6 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="text-emerald-500" size={18} />
                    <h3 className="font-semibold text-foreground">Improvement Potential</h3>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{analysis.improvementPotential.carelessFixable}</p>
                      <p className="text-xs text-muted-foreground">Quick Wins (Careless)</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{analysis.improvementPotential.conceptualNeedStudy}</p>
                      <p className="text-xs text-muted-foreground">Need Deep Study</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{analysis.improvementPotential.timeManagement}</p>
                      <p className="text-xs text-muted-foreground">Time-Related</p>
                    </div>
                  </div>
                </Card>

                {/* Difficulty Breakdown */}
                <Card className="p-4 mb-6">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Target size={18} className="text-primary" />
                    Mistake Difficulty Distribution
                  </h3>
                  <div className="space-y-3">
                    {[
                      { label: 'Easy', value: analysis.difficultyBreakdown.easy, color: 'bg-green-500', lightColor: 'bg-green-100 dark:bg-green-900/30' },
                      { label: 'Medium', value: analysis.difficultyBreakdown.medium, color: 'bg-yellow-500', lightColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
                      { label: 'Hard', value: analysis.difficultyBreakdown.hard, color: 'bg-red-500', lightColor: 'bg-red-100 dark:bg-red-900/30' }
                    ].map(item => {
                      const percentage = mistakes.length > 0 ? Math.round((item.value / mistakes.length) * 100) : 0;
                      return (
                        <div key={item.label} className="flex items-center gap-3">
                          <span className="text-sm font-medium w-16">{item.label}</span>
                          <div className={`flex-1 h-6 rounded-full ${item.lightColor} overflow-hidden`}>
                            <div 
                              className={`h-full ${item.color} rounded-full transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold w-16 text-right">{item.value} ({percentage}%)</span>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {/* Weak Areas */}
                {analysis.weakAreas.length > 0 && (
                  <Card className="p-4 mb-6 bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="text-amber-500" size={18} />
                      <h3 className="font-semibold text-foreground">Priority Focus Areas</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {analysis.weakAreas.map((area, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setActiveTab('questions');
                            setFilterSubject(area.subject);
                            setFilterChapter(area.name);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white dark:bg-gray-800 border-amber-300 dark:border-amber-700 hover:border-amber-500 transition-all text-sm"
                        >
                          <span className="font-medium">{area.name}</span>
                          <Badge variant="destructive" className="text-xs px-1.5 py-0">{area.count}</Badge>
                        </button>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Subject Breakdown */}
                <Card className="p-4 mb-6">
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <GraduationCap size={18} className="text-primary" />
                    Subject-wise Breakdown
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(analysis.subjects || []).slice(0, 8).map((subject, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setActiveTab('questions');
                          setFilterSubject(filterSubject === subject.name ? 'all' : subject.name);
                        }}
                        className="p-3 rounded-xl border bg-muted/30 border-border hover:border-primary/50 text-left transition-all"
                      >
                        <p className="font-medium text-foreground text-sm truncate">{subject.name}</p>
                        <p className="text-lg font-bold text-primary">{subject.count}</p>
                        <div className="flex gap-1 mt-1">
                          <span className="text-[10px] px-1 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">{subject.difficulties?.easy || 0} E</span>
                          <span className="text-[10px] px-1 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400">{subject.difficulties?.medium || 0} M</span>
                          <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">{subject.difficulties?.hard || 0} H</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </Card>
              </>
            )}

            {/* Test Mistakes Tab */}
            {activeTab === 'test-mistakes' && (
              <>
                <div className="flex items-center gap-3 mb-6 p-4 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border border-purple-200 dark:border-purple-800">
                  <Trophy className="text-purple-500" size={24} />
                  <div>
                    <h3 className="font-bold text-foreground text-lg">Test Mistakes</h3>
                    <p className="text-sm text-muted-foreground">Mistakes made during mock tests and assessments</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-3xl font-bold text-purple-600">{mistakes.filter(m => m.source_type === 'test').length}</p>
                    <p className="text-xs text-muted-foreground">Total found</p>
                  </div>
                </div>

                {renderMistakesSection('test')}
              </>
            )}

            {/* Practice Mistakes Tab */}
            {activeTab === 'practice-mistakes' && (
              <>
                <div className="flex items-center gap-3 mb-6 p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border border-emerald-200 dark:border-emerald-800">
                  <Target className="text-emerald-500" size={24} />
                  <div>
                    <h3 className="font-bold text-foreground text-lg">Practice Mistakes</h3>
                    <p className="text-sm text-muted-foreground">Mistakes made during practice sessions</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-3xl font-bold text-emerald-600">{mistakes.filter(m => m.source_type === 'practice').length}</p>
                    <p className="text-xs text-muted-foreground">Total found</p>
                  </div>
                </div>

                {renderMistakesSection('practice')}
              </>
            )}

            {/* Patterns Tab */}
            {activeTab === 'patterns' && (
              <>
                {/* Mistake Type Analysis */}
                <Card className="p-4 mb-6">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Brain size={18} className="text-primary" />
                    Mistake Type Analysis
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-center">
                      <Zap className="text-amber-500 mx-auto mb-2" size={24} />
                      <p className="text-2xl font-bold text-amber-600">{analysis.mistakeTypes.careless}</p>
                      <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Careless</p>
                      <p className="text-xs text-muted-foreground mt-1">Easy mistakes on easy questions</p>
                    </div>
                    <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 text-center">
                      <BookOpen className="text-purple-500 mx-auto mb-2" size={24} />
                      <p className="text-2xl font-bold text-purple-600">{analysis.mistakeTypes.conceptual}</p>
                      <p className="text-sm font-medium text-purple-700 dark:text-purple-400">Conceptual</p>
                      <p className="text-xs text-muted-foreground mt-1">Need deeper understanding</p>
                    </div>
                    <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-center">
                      <Clock className="text-blue-500 mx-auto mb-2" size={24} />
                      <p className="text-2xl font-bold text-blue-600">{analysis.mistakeTypes.timeRelated}</p>
                      <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Time Pressure</p>
                      <p className="text-xs text-muted-foreground mt-1">Rushed or overthought</p>
                    </div>
                    <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-center">
                      <RefreshCw className="text-red-500 mx-auto mb-2" size={24} />
                      <p className="text-2xl font-bold text-red-600">{analysis.repeatMistakes.length}</p>
                      <p className="text-sm font-medium text-red-700 dark:text-red-400">Repeat Mistakes</p>
                      <p className="text-xs text-muted-foreground mt-1">Made same mistake multiple times</p>
                    </div>
                  </div>
                </Card>

                {/* Recency Analysis */}
                <Card className="p-4 mb-6">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Calendar size={18} className="text-primary" />
                    Mistake Timeline
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-center">
                      <TrendingUp className="text-red-500 mx-auto mb-2" size={24} />
                      <p className="text-2xl font-bold text-red-600">{analysis.recentMistakes.length}</p>
                      <p className="text-sm text-muted-foreground">Last 7 Days</p>
                      <p className="text-xs text-red-600 mt-1">Needs immediate attention</p>
                    </div>
                    <div className="p-4 rounded-xl bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 text-center">
                      <BarChart3 className="text-yellow-500 mx-auto mb-2" size={24} />
                      <p className="text-2xl font-bold text-yellow-600">{mistakes.length - analysis.recentMistakes.length - analysis.oldMistakes.length}</p>
                      <p className="text-sm text-muted-foreground">Past Month</p>
                      <p className="text-xs text-yellow-600 mt-1">Review recommended</p>
                    </div>
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 text-center">
                      <TrendingDown className="text-gray-500 mx-auto mb-2" size={24} />
                      <p className="text-2xl font-bold text-gray-600">{analysis.oldMistakes.length}</p>
                      <p className="text-sm text-muted-foreground">Older than 30 Days</p>
                      <p className="text-xs text-green-600 mt-1">May have improved</p>
                    </div>
                  </div>
                </Card>

                {/* Top Problem Chapters */}
                <Card className="p-4 mb-6">
                  <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Flame size={18} className="text-orange-500" />
                    Most Problematic Topics
                  </h3>
                  <div className="space-y-3">
                    {(analysis.chapters || []).slice(0, 5).map((chapter, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-500' : idx === 2 ? 'bg-yellow-500' : 'bg-gray-400'
                        }`}>
                          <span className="text-white font-bold text-sm">#{idx + 1}</span>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{chapter.name}</p>
                          <p className="text-xs text-muted-foreground">{chapter.subject}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-foreground">{chapter.count}</p>
                          <p className="text-xs text-muted-foreground">mistakes</p>
                        </div>
                        <div className="flex gap-1">
                          <span className="text-[10px] px-1 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700">{chapter.difficulties.easy}</span>
                          <span className="text-[10px] px-1 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700">{chapter.difficulties.medium}</span>
                          <span className="text-[10px] px-1 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700">{chapter.difficulties.hard}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            )}

            {/* Spaced Repetition Tab */}
            {activeTab === 'spaced' && (
              <>
                {/* Due for Review */}
                <Card className="p-4 mb-6 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30 border-red-200 dark:border-red-800">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="text-red-500" size={18} />
                      <h3 className="font-semibold text-foreground">Due for Review Now ({analysis.dueForReview.length})</h3>
                    </div>
                    {analysis.dueForReview.length > 0 && (
                      <Button 
                        size="sm" 
                        onClick={handleGenerateRevisionTest}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        <RefreshCw size={14} className="mr-1" />
                        Review All
                      </Button>
                    )}
                  </div>
                  {analysis.dueForReview.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {analysis.dueForReview.slice(0, 10).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            item.priority === 'high' ? 'bg-red-500' : 'bg-orange-500'
                          }`}>
                            <span className="text-white text-xs font-bold">{idx + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {item.question?.chapter || 'Mixed Topics'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {item.question?.question_text?.substring(0, 60)}...
                            </p>
                          </div>
                          <Badge variant={item.priority === 'high' ? 'destructive' : 'warning'} className="text-xs">
                            {item.daysSinceMistake}d ago
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <CheckCircle className="text-green-500 mx-auto mb-2" size={32} />
                      <p className="text-muted-foreground">All caught up! No questions due for review.</p>
                    </div>
                  )}
                </Card>

                {/* Upcoming Reviews */}
                <Card className="p-4 mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="text-blue-500" size={18} />
                    <h3 className="font-semibold text-foreground">Coming Up This Week ({analysis.upcomingReview.length})</h3>
                  </div>
                  {analysis.upcomingReview.length > 0 ? (
                    <div className="space-y-2">
                      {analysis.upcomingReview.slice(0, 6).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800">
                          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                            <Clock className="text-white" size={14} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {item.question?.chapter || 'Mixed Topics'}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            In {item.daysUntilDue} day{item.daysUntilDue !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">No reviews scheduled for this week.</p>
                  )}
                </Card>

                {/* Spaced Repetition Info */}
                <Card className="p-4 bg-muted/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="text-amber-500" size={18} />
                    <h3 className="font-semibold text-foreground">How Spaced Repetition Works</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Questions you get wrong are scheduled for review at increasing intervals (1, 2, 3, 5, 8, 13, 21 days).
                    This optimizes retention by reviewing just before you're likely to forget.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 5, 8, 13, 21].map((day, idx) => (
                      <div key={day} className="px-2 py-1 rounded bg-primary/10 text-xs font-medium text-primary">
                        Day {day}
                      </div>
                    ))}
                  </div>
                </Card>
              </>
            )}

            {/* Questions Tab */}
            {activeTab === 'questions' && (
              <>
                {/* Filters */}
                <Card className="p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-foreground flex items-center gap-2">
                      <Filter size={16} />
                      Filter Questions
                    </h3>
                    {hasActiveFilters && (
                      <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                        Clear Filters
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {/* Source Type Filter */}
                    <select
                      value={filterSourceType}
                      onChange={(e) => setFilterSourceType(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                    >
                      <option value="all">All Sources</option>
                      <option value="test">From Tests</option>
                      <option value="practice">From Practice</option>
                    </select>

                    {/* Exam Filter */}
                    <select
                      value={filterExam}
                      onChange={(e) => {
                        setFilterExam(e.target.value);
                        setFilterSubject('all');
                        setFilterChapter('all');
                      }}
                      className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                    >
                      <option value="all">All Exams ({mistakes.length})</option>
                      {(analysis.exams || []).map(exam => (
                        <option key={exam.id} value={exam.id}>{exam.name} ({exam.count})</option>
                      ))}
                    </select>

                    {/* Subject Filter */}
                    <select
                      value={filterSubject}
                      onChange={(e) => {
                        setFilterSubject(e.target.value);
                        setFilterChapter('all');
                      }}
                      className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                    >
                      <option value="all">All Subjects</option>
                      {availableSubjects.map(subject => (
                        <option key={subject.name} value={subject.name}>{subject.name} ({subject.count})</option>
                      ))}
                    </select>

                    {/* Chapter Filter */}
                    <select
                      value={filterChapter}
                      onChange={(e) => setFilterChapter(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                    >
                      <option value="all">All Chapters</option>
                      {availableChapters.map(chapter => (
                        <option key={`${chapter.subject}__${chapter.name}`} value={chapter.name}>
                          {chapter.name} ({chapter.count})
                        </option>
                      ))}
                    </select>

                    {/* Mistake Type Filter */}
                    <select
                      value={filterMistakeType}
                      onChange={(e) => setFilterMistakeType(e.target.value)}
                      className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                    >
                      <option value="all">All Types</option>
                      <option value="careless">Careless Errors ({analysis.mistakeTypes.careless})</option>
                      <option value="conceptual">Conceptual ({analysis.mistakeTypes.conceptual})</option>
                      <option value="rushed">Rushed ({analysis.mistakeTypes.timeRelated})</option>
                    </select>
                  </div>
                </Card>

                {/* Questions List */}
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">
                    All Mistake Questions ({filteredMistakes.length})
                  </h3>
                  {hasActiveFilters && (
                    <Badge variant="outline">Filtered</Badge>
                  )}
                </div>

            <div className="space-y-3">
              {filteredMistakes.slice(0, isPremium ? 100 : 10).map((item, idx) => {
                const q = item.question;
                if (!q) return null;
                
                const isExpanded = expandedQuestions[item.id];
                const difficulty = q.difficulty || 'medium';
                const subjectName = q.subject_name || q.subject_id || 'General';
                const chapterName = q.chapter || 'Mixed Topics';

                return (
                  <Card key={item.id} className="overflow-hidden border border-border">
                    {/* Question Header - Always Visible */}
                    <button
                      onClick={() => toggleQuestion(item.id)}
                      className="w-full p-4 text-left flex items-start gap-3 hover:bg-muted/30 transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        difficulty === 'easy' ? 'bg-green-100 dark:bg-green-900/30' :
                        difficulty === 'hard' ? 'bg-red-100 dark:bg-red-900/30' :
                        'bg-yellow-100 dark:bg-yellow-900/30'
                      }`}>
                        <span className="text-xs font-bold text-foreground">{idx + 1}</span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant="outline" className="text-[10px]">{subjectName}</Badge>
                          <Badge variant="secondary" className="text-[10px]">{chapterName}</Badge>
                          <Badge className={`text-[10px] ${
                            difficulty === 'easy' ? 'bg-green-500' :
                            difficulty === 'hard' ? 'bg-red-500' : 'bg-yellow-500'
                          }`}>{difficulty}</Badge>
                          {q.year && <span className="text-[10px] text-muted-foreground">PYQ {q.year}</span>}
                        </div>
                        <p 
                          className="text-sm text-foreground line-clamp-3"
                          dangerouslySetInnerHTML={{ __html: renderLatex(q.question_text || '') }}
                        />
                      </div>

                      <div className="shrink-0">
                        {isExpanded ? <ChevronUp size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
                      </div>
                    </button>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-border bg-muted/20">
                        {/* Full Question */}
                        <div className="py-4">
                          <p 
                            className="text-sm text-foreground leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: renderLatex(q.question_text) }}
                          />
                        </div>

                        {/* Options */}
                        {q.options && Array.isArray(q.options) && q.options.length > 0 && (
                          <div className="space-y-2 mb-4">
                            {q.options.map((option, optIdx) => {
                              const optionLetter = option.label || String.fromCharCode(65 + optIdx);
                              const optionText = option.text || option;
                              const isCorrect = q.correct_answer === optionLetter;
                              const userAnswer = item.user_answer || item.mistake?.user_answer;
                              const isUserAnswer = userAnswer === optionLetter;
                              const isWrong = isUserAnswer && !isCorrect;

                              return (
                                <div 
                                  key={optIdx}
                                  className={`flex items-start gap-3 p-3 rounded-lg ${
                                    isCorrect 
                                      ? 'bg-green-50 dark:bg-green-950/30 border border-green-300 dark:border-green-700' 
                                      : isWrong 
                                      ? 'bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-700'
                                      : 'bg-muted/50 border border-border'
                                  }`}
                                >
                                  <span className={`font-bold text-sm ${
                                    isCorrect ? 'text-green-600' : isWrong ? 'text-red-600' : 'text-muted-foreground'
                                  }`}>
                                    {optionLetter}.
                                  </span>
                                  <span 
                                    className={`flex-1 text-sm ${
                                      isCorrect ? 'text-green-700 dark:text-green-300' : 
                                      isWrong ? 'text-red-700 dark:text-red-300' : 'text-foreground'
                                    }`}
                                    dangerouslySetInnerHTML={{ __html: renderLatex(optionText) }}
                                  />
                                  {isCorrect && <CheckCircle className="text-green-500 shrink-0" size={18} />}
                                  {isWrong && <XCircle className="text-red-500 shrink-0" size={18} />}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Numerical Answer */}
                        {(!q.options || q.options.length === 0) && (
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                              <p className="text-xs font-medium text-red-600 mb-1">Your Answer</p>
                              <p className="text-lg font-bold text-red-700 dark:text-red-300">
                                {item.user_answer || item.mistake?.user_answer || '—'}
                              </p>
                            </div>
                            <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                              <p className="text-xs font-medium text-green-600 mb-1">Correct Answer</p>
                              <p className="text-lg font-bold text-green-700 dark:text-green-300">
                                {q.correct_answer}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Explanation */}
                        {q.explanation && (
                          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                            <div className="flex items-center gap-2 mb-2">
                              <Lightbulb className="text-blue-500" size={16} />
                              <span className="text-sm font-medium text-blue-600">Explanation</span>
                            </div>
                            <div 
                              className="text-sm text-foreground leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: renderLatex(q.explanation) }}
                            />
                          </div>
                        )}

                        {/* Attempt Info */}
                        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                          <span>Attempts: {item.attempts || 1}</span>
                          {item.created_at && (
                            <span>Last: {new Date(item.created_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}

              {/* Premium Upsell */}
              {!isPremium && filteredMistakes.length > 10 && (
                <Card className="p-6 text-center bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-300 dark:border-amber-700">
                  <Lock className="text-amber-500 mx-auto mb-3" size={32} />
                  <h3 className="font-bold text-foreground mb-1">
                    +{filteredMistakes.length - 10} more questions hidden
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Upgrade to Premium to see all mistakes and get AI-powered revision tests
                  </p>
                  <Button onClick={() => navigate('/subscription')} className="bg-amber-500 hover:bg-amber-600">
                    Upgrade to Premium
                  </Button>
                </Card>
              )}

              {filteredMistakes.length === 0 && hasActiveFilters && (
                <Card className="p-8 text-center border-dashed">
                  <p className="text-muted-foreground mb-2">No mistakes found with these filters</p>
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </Card>
              )}
            </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Smart Revision Modal */}
      <Dialog open={showRevisionModal} onOpenChange={setShowRevisionModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="text-primary" size={24} />
              Smart Revision - Select Topics
            </DialogTitle>
            <DialogDescription>
              Choose the subjects and chapters you want to practice. A custom test will be generated based on your mistakes in these topics.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const allSubjects = Object.values(analysis.subjects || []).map(s => s.name);
                  setRevisionSubjects(allSubjects);
                  const allChapters = Object.values(analysis.chapters || []).map(c => c.name);
                  setRevisionChapters(allChapters);
                }}
              >
                Select All
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setRevisionSubjects([]);
                  setRevisionChapters([]);
                }}
              >
                Clear All
              </Button>
            </div>

            {/* Number of Questions */}
            <div className="space-y-2">
              <Label htmlFor="numQuestions">Number of Questions</Label>
              <input
                id="numQuestions"
                type="number"
                min="5"
                max="50"
                value={revisionNumQuestions}
                onChange={(e) => setRevisionNumQuestions(parseInt(e.target.value) || 10)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              />
            </div>

            {/* Subject Selection */}
            <div className="space-y-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <BookOpen size={18} className="text-primary" />
                Select Subjects & Chapters
              </h3>

              {Object.values(analysis.subjects || [])
                .sort((a, b) => b.count - a.count)
                .map((subject) => {
                  const isSubjectSelected = revisionSubjects.includes(subject.name);
                  const subjectChapters = Object.values(analysis.chapters || [])
                    .filter(c => c.subject === subject.name)
                    .sort((a, b) => b.count - a.count);

                  return (
                    <Card key={subject.name} className="p-4">
                      {/* Subject Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <Checkbox
                          id={`subject-${subject.name}`}
                          checked={isSubjectSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setRevisionSubjects([...revisionSubjects, subject.name]);
                              // Auto-select all chapters of this subject
                              const chapterNames = subjectChapters.map(c => c.name);
                              setRevisionChapters([...new Set([...revisionChapters, ...chapterNames])]);
                            } else {
                              setRevisionSubjects(revisionSubjects.filter(s => s !== subject.name));
                              // Deselect all chapters of this subject
                              const chapterNames = subjectChapters.map(c => c.name);
                              setRevisionChapters(revisionChapters.filter(c => !chapterNames.includes(c)));
                            }
                          }}
                        />
                        <Label
                          htmlFor={`subject-${subject.name}`}
                          className="flex-1 cursor-pointer font-semibold text-foreground"
                        >
                          {subject.name}
                        </Label>
                        <Badge variant="secondary">{subject.count} mistakes</Badge>
                        <div className="flex gap-1">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700">
                            {subject.difficulties.easy}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700">
                            {subject.difficulties.medium}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700">
                            {subject.difficulties.hard}
                          </span>
                        </div>
                      </div>

                      {/* Chapters */}
                      {subjectChapters.length > 0 && (
                        <div className="ml-8 space-y-2 border-l-2 border-border pl-4">
                          {subjectChapters.map((chapter) => {
                            const isChapterSelected = revisionChapters.includes(chapter.name);
                            return (
                              <div key={chapter.name} className="flex items-center gap-3">
                                <Checkbox
                                  id={`chapter-${subject.name}-${chapter.name}`}
                                  checked={isChapterSelected}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setRevisionChapters([...revisionChapters, chapter.name]);
                                      // Auto-select parent subject
                                      if (!revisionSubjects.includes(subject.name)) {
                                        setRevisionSubjects([...revisionSubjects, subject.name]);
                                      }
                                    } else {
                                      setRevisionChapters(revisionChapters.filter(c => c !== chapter.name));
                                    }
                                  }}
                                />
                                <Label
                                  htmlFor={`chapter-${subject.name}-${chapter.name}`}
                                  className="flex-1 cursor-pointer text-sm text-foreground"
                                >
                                  {chapter.name}
                                </Label>
                                <Badge variant="outline" className="text-xs">{chapter.count}</Badge>
                                <div className="flex gap-1">
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-600">
                                    {chapter.difficulties.easy}
                                  </span>
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600">
                                    {chapter.difficulties.medium}
                                  </span>
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-red-50 dark:bg-red-900/20 text-red-600">
                                    {chapter.difficulties.hard}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  );
                })}

              {Object.keys(analysis.subjects || {}).length === 0 && (
                <Card className="p-8 text-center border-dashed">
                  <p className="text-muted-foreground">No mistakes available for revision</p>
                </Card>
              )}
            </div>

            {/* Preview */}
            {(revisionSubjects.length > 0 || revisionChapters.length > 0) && (
              <Card className="p-4 bg-primary/5 border-primary/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">
                      {revisionSubjects.length} subject{revisionSubjects.length !== 1 ? 's' : ''}, {' '}
                      {revisionChapters.length} chapter{revisionChapters.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Generating {revisionNumQuestions} questions
                    </p>
                  </div>
                  <TrendingUp className="text-primary" size={32} />
                </div>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRevisionModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={generateRevisionTest}
              disabled={revisionSubjects.length === 0 && revisionChapters.length === 0}
              className="bg-primary hover:bg-primary/90"
            >
              <TrendingUp size={16} className="mr-2" />
              Generate Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Mistakes;
